/**
 * What: The right pane of the field picker for the picked field: its full value, then one "Test this field as" choice
 *   (round buttons, each concept with its description, suggested first). A Shopify-own field's concept is fixed, so it is
 *   shown as plain text, not a choice. The shopper questions the test asks are shown once by the standard "Shopper
 *   questions" section, never previewed here.
 * Transport / data-flow: Dumb renderer over one `field-picker` row. Picking a concept calls `onChoose` with that choice's
 *   finished `comparisons.add` edit, which the pane reports verbatim; the app composes nothing.
 */
import type { BuilderBlock, BuilderEdit } from '@testfeed/shopify-contracts'
import { ReviewFieldValues } from './ReviewFieldValues'

type FieldPickerBlock = Extract<BuilderBlock, { kind: 'field-picker' }>
type Row = FieldPickerBlock['rows'][number]

/** Generic display state comes from the server; the browser does not classify concepts. */
function ChoiceBadge({ choice }: { choice: Row['choices'][number] }) {
  if (choice.unavailableLabel) return <s-badge>{choice.unavailableLabel}</s-badge>
  if (choice.suggestedBadge) return <s-badge tone="success">{choice.suggestedBadge}</s-badge>
  return null
}

function ConceptChoice({
  choice,
  selectedConceptId,
  disabled,
  onChoose,
}: {
  choice: Row['choices'][number]
  selectedConceptId: Row['selectedConceptId']
  disabled: boolean
  onChoose: (edit: BuilderEdit) => void
}) {
  const unavailable = disabled || choice.disabled
  const values = selectedConceptId === choice.conceptId ? [choice.conceptId] : []
  return (
    <s-grid gridTemplateColumns="minmax(0, 1fr) auto" gap="small-100" alignItems="start">
      <s-choice-list
        name="field-test-as"
        values={values}
        disabled={unavailable}
        onInput={() => {
          if (!unavailable) onChoose(choice.edit)
        }}
      >
        <s-choice value={choice.conceptId}>
          {choice.label}
          <s-text slot="details" color="subdued">
            {choice.description}
          </s-text>
        </s-choice>
      </s-choice-list>
      <ChoiceBadge choice={choice} />
    </s-grid>
  )
}

function TestAs({
  block,
  row,
  disabled,
  onChoose,
}: {
  block: FieldPickerBlock
  row: Row
  disabled: boolean
  onChoose: (edit: BuilderEdit) => void
}) {
  return (
    <s-stack direction="block" gap="small-200">
      <s-text type="strong">{block.testAsHeading}</s-text>
      {row.choices.length === 0 ? (
        <s-text color="subdued">{block.noConceptsLabel}</s-text>
      ) : (
        // Polaris lays out slotted choice content itself (padding on it is ignored), so each concept is its own
        // one-choice list in a stack: the stack gap gives the row spacing, and the controlled values make them act as one.
        <s-stack direction="block" gap="base">
          {row.choices.map((choice) => (
            <ConceptChoice
              key={choice.conceptId}
              choice={choice}
              selectedConceptId={row.selectedConceptId}
              disabled={disabled}
              onChoose={onChoose}
            />
          ))}
        </s-stack>
      )}
    </s-stack>
  )
}

export function ReviewFieldDetail({
  block,
  row,
  disabled,
  onChoose,
}: {
  block: FieldPickerBlock
  row: Row
  disabled: boolean
  onChoose: (edit: BuilderEdit) => void
}) {
  return (
    <s-stack direction="block" gap="base">
      <ReviewFieldValues heading={block.valuesHeading} preview={row.preview} note={row.valuesNote} />
      <s-divider />
      <TestAs block={block} row={row} disabled={disabled} onChoose={onChoose} />
    </s-stack>
  )
}
